import { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import CustomTable from './CustomTable';
import { createColumnHelper } from '@tanstack/react-table';
import { FileContext } from '../../../context/FileContext';
import { useEnvelope } from '../../../hooks/useEnvelope';

import { applySubItemFilters } from '../../../utils/common';
import { useMondayStore } from '../../../store/monday.store';
import { formatNumber } from '@/utils/controlSidebar/number-formatting.utils';

const MainContainer = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  gap: ${({ isPreview }) => (isPreview ? '0px' : '20px')};
  font-family: 'Open Sans', sans-serif;

  .tax-row {
    text-align: right;
    padding: 10px 0 10px 0;
    border-top: 1px solid black !important;
  }
  .sum-row {
    text-align: right;
    padding: 10px 0 10px 0;
    border-top: ${({ hasTax }) => (hasTax ? '' : '1px solid black !important')};
  }
  .min-width-span {
    min-width: 30px;
    display: inline-block; /* Ensures that the minimum width is respected */
  }

  .title {
    color: var(--light-primary-text-color, #323338);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    padding-right: 10px;
  }
`;

const LineItemsPreview = ({
  spaceInFirstPage,
  lineItemData,
  lineItemId,
  isResizable = false,
  tableHeaderRef,
  isPreview = false,
  width,
  pageNumber,
  t,
  onColumnResizeStart = () => {},
  onColumnResizeEnd = () => {},
  setColumnPercentages = () => {},
}) => {
  const columnHelper = createColumnHelper();
  const [columns, setColumns] = useState([]);
  const [tableData, setTableData] = useState([]);
  const { headerData, lineItemsRowHeights, pdfIndividualPageDetails } =
    useContext(FileContext);

  const [updatedTableData, setUpdatedTableData] = useState(tableData);

  const { addPageInEnvelopePDF } = useEnvelope();
  const subItems = useMondayStore.getState().subitems;

  useEffect(() => {
    if (subItems?.length) {
      const data = subItems;
      const transformedData = data.map((item) => {
        const transformedItem = {};
        item.column_values.forEach((column) => {
          if (column?.type === 'formula') {
            transformedItem[column.id] = column.display_value;
          } else if (column?.type === 'numbers') {
            transformedItem[column.id] = column.text;
          } else {
            transformedItem[column.id] = column.text;
          }
        });
        transformedItem['item-name'] = item.name;
        return transformedItem;
      });

      const filteredData = applySubItemFilters(
        transformedData,
        lineItemData?.filterConfig || {
          isFilterEnabled: false,
          filterChaining: '',
          filters: [],
        },
      );
      setTableData(filteredData);
    }
  }, [subItems, lineItemData?.filterConfig]);

  const calculateNewTableDataWithSpaces = async ({
    dynamicRowHeights,
    pdfIndividualPageDetails,
    spaceInFirstPage,
    pageNumber,
  }) => {
    let totalRemainingSpageInPage = spaceInFirstPage - 30;
    let updatedTableDataArray = [];
    let pageIndex = 0;
    let page = pageNumber;
    let pagesNeededCount = 0;
    const PAGE_HEIGHT = 841.8898 - 30; // Added -30 to have some space at the bottom of the page

    tableData.forEach((x, index) => {
      const newRowHeight = dynamicRowHeights[index];
      if (Math.sign(totalRemainingSpageInPage - newRowHeight) === -1) {
        const previousRemainingSpace = totalRemainingSpageInPage;

        page += 1;

        // Check if we need to add a new page
        if (page > pdfIndividualPageDetails.length) {
          pagesNeededCount++;
        }

        // Set remaining space to the new page's height
        totalRemainingSpageInPage = PAGE_HEIGHT;

        updatedTableDataArray.push({
          id: pageIndex + index,
          size: 36 + previousRemainingSpace,
          isEmpty: true,
        });
        pageIndex += 1;
      }

      updatedTableDataArray.push(x);
      totalRemainingSpageInPage -= newRowHeight;
    });

    if (pagesNeededCount > 0) {
      for (let i = 0; i < pagesNeededCount; i++) {
        await addPageInEnvelopePDF(pdfIndividualPageDetails.length);
      }
    }

    setUpdatedTableData(updatedTableDataArray);
  };

  useEffect(() => {
    if (
      lineItemsRowHeights[lineItemId]?.length &&
      tableData?.length &&
      pdfIndividualPageDetails
    ) {
      const updateTableData = async () => {
        await calculateNewTableDataWithSpaces({
          dynamicRowHeights: lineItemsRowHeights[lineItemId],
          pdfIndividualPageDetails,
          spaceInFirstPage,
          pageNumber,
        });
      };

      updateTableData();
    }
  }, [
    lineItemsRowHeights,
    tableData,
    pdfIndividualPageDetails,
    spaceInFirstPage,
    pageNumber,
    lineItemId,
  ]);

  useEffect(() => {
    if (tableData) {
      setUpdatedTableData(tableData);
    }
  }, [tableData]);

  const isValidFileExtensionType = (url) => {
    //check for valid extensions
    const supportedExtensions = ['jpg', 'jpeg', 'png', 'JPG', 'PNG', 'JPEG'];
    let isValid = false;
    if (url) {
      const splittedUrl = url.split('/');
      const fileName = splittedUrl.at(-1);
      const fileExtension = fileName.split('.').at(-1);
      isValid = supportedExtensions.includes(fileExtension);
    }

    return isValid;
  };

  const createDynamicColumns = (col, index, size) => {
    const options = {
      cell: (info) => {
        const columnSettings = JSON.parse(col.settings_str || '{}');
        if (col.type === 'file')
          return (
            <>
              {info
                .getValue()
                ?.split(',')
                .map((x) => (
                  <>
                    {isValidFileExtensionType(x) ? (
                      <img
                        src={x}
                        width={50}
                        style={{ marginRight: '8px' }}
                        alt="file"
                      />
                    ) : null}
                  </>
                ))}
            </>
          );
        if (col.type === 'numbers') {
          return (
            <span>
              {formatNumber({
                text: info.getValue(),
                formatOptions: columnSettings,
              })}
            </span>
          );
        }

        return <span>{info.getValue()}</span>;
      },
      header: () => <span>{col.label}</span>,
      footer: (info) => info.column.id,
      size: size || 150,
    };

    return columnHelper.accessor(col.id, {
      ...options,
    });
  };

  const calculateTotalSum = (updatedTableData, lineItemData) => {
    const subColumnSettingsStr = lineItemData.columns.find(
      (x) => x.id === lineItemData.sum.column?.id,
    )?.column?.settings_str;
    let subColumnSettings = {};
    try {
      subColumnSettings = JSON.parse(subColumnSettingsStr || '{}');
    } catch (error) {
      console.error('Failed to parse column settings:', error);
      subColumnSettings = {};
    }
    const subColumnId = lineItemData?.sum?.column?.id;
    if (!lineItemData?.sum?.checked || !subColumnId) return;

    const sum = updatedTableData.reduce((acc, curr) => {
      const value = curr[subColumnId] || 0;
      return acc + parseFloat(value);
    }, 0);

    let finalSum = sum;
    if (lineItemData?.tax?.checked) {
      if (lineItemData?.tax?.type?.value === 'percentage') {
        finalSum += (parseFloat(lineItemData?.tax?.value) / 100) * finalSum;
      }
      if (lineItemData?.tax?.type?.value === 'fixed') {
        finalSum += parseFloat(lineItemData?.tax?.value);
      }
    }
    const formattedSum = formatNumber({
      text: finalSum.toString(),
      formatOptions: subColumnSettings,
    });
    return formattedSum;
  };

  useEffect(() => {
    if (lineItemData.columnWidth && width) {
      const columnWidth = lineItemData.columnWidth;

      const headers = [];

      if (lineItemData?.itemNameColumn?.checked) {
        const itemNameWidthPct = columnWidth['item-name'] ?? 0;
        headers.push(
          columnHelper.accessor('item-name', {
            cell: (info) => info.getValue(),
            footer: (info) => info.column.id,
            header: () => <span>{t('Item')}</span>,
            size: (itemNameWidthPct * width) / 100,
          }),
        );
      }
      const updatedHeaders = lineItemData?.columns.map((x) => {
        return {
          ...x,
          extraData: headerData?.find((y) => y.id === x.id) || {},
        };
      });
      updatedHeaders.forEach((column, index) => {
        const newColumn = createDynamicColumns(
          column,
          index,
          (columnWidth[column.id] * width) / 100,
        );
        headers.push(newColumn);
      });
      setColumns([...headers]);
    }
  }, [lineItemData, width]);

  return (
    <MainContainer hasTax={lineItemData?.tax.checked} isPreview={isPreview}>
      <CustomTable
        data={[...updatedTableData]}
        columns={[...columns]}
        lineItemData={lineItemData}
        isResizable={isResizable}
        tableHeaderRef={tableHeaderRef}
        lineItemId={lineItemId}
        onColumnResizeStart={onColumnResizeStart}
        onColumnResizeEnd={onColumnResizeEnd}
        setColumnPercentages={setColumnPercentages}
      />
      {lineItemData?.tax.checked ? (
        <div className="tax-row">
          <span className="title">{lineItemData.tax.label || 'Tax'}</span>{' '}
          <span className="min-width-span">
            {lineItemData.tax.value || 0}
            {lineItemData.tax.type?.value === 'percentage' ? '%' : null}
          </span>
        </div>
      ) : null}
      {lineItemData.sum.checked ? (
        <div className="sum-row">
          <span className="title">{lineItemData.sum.label || 'Sum'}</span>{' '}
          <span className="min-width-span">
            {lineItemData.currency.checked &&
            lineItemData.currency.position?.value === 'before'
              ? lineItemData.currency.type?.symbol
              : null}
            {calculateTotalSum(updatedTableData, lineItemData)}
            {lineItemData.currency.checked &&
            lineItemData.currency.position?.value === 'after'
              ? lineItemData.currency.type?.symbol
              : null}
          </span>
        </div>
      ) : null}
    </MainContainer>
  );
};

export default LineItemsPreview;
